import { useEffect } from 'react';

/**
 * Confirmar algo que no borra nada.
 *
 * Existe porque marcar un trabajo como facturado se estaba preguntando con el
 * diálogo de borrado: decía «¿Querés borrar…?» y el botón decía «Borrar», en
 * rojo. Nadie está borrando nada. Un cartel rojo que miente sobre lo que va a
 * pasar hace dos daños: asusta cuando no corresponde y, sobre todo, gasta el
 * rojo. Cuando aparezca el borrado de verdad ya nadie lo va a mirar.
 *
 * Es la misma hoja de abajo que `ConfirmarBorrado` —el pulgar cae ahí, y en el
 * teléfono un diálogo centrado obliga a estirar la mano— pero sin la palabra a
 * tipear y sin el rojo. Lo que sí conserva es decir qué va a pasar antes de que
 * pase.
 */
export function ConfirmarAccion({
  titulo,
  pregunta,
  detalle,
  confirmar = 'Confirmar',
  ocupado = false,
  onConfirmar,
  onCancelar,
  testId = 'confirmar-accion',
}: {
  titulo: string;
  pregunta: string;
  /** La consecuencia, en una línea. Lo que hay que saber ANTES de confirmar. */
  detalle?: string;
  confirmar?: string;
  ocupado?: boolean;
  onConfirmar: () => void;
  onCancelar: () => void;
  testId?: string;
}) {
  useEffect(() => {
    const alTeclado = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancelar();
    };
    window.addEventListener('keydown', alTeclado);
    return () => window.removeEventListener('keydown', alTeclado);
  }, [onCancelar]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
      onClick={onCancelar}
      data-testid={testId}
    >
      <div
        className="zona-inferior margen-lateral-seguro w-full max-w-2xl rounded-t-2xl border-t border-[var(--linea)] bg-[var(--superficie)] p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-[var(--linea-fuerte)]" aria-hidden />

        <h2 className="text-base font-medium">{titulo}</h2>
        <p className="mt-1 text-sm">{pregunta}</p>
        {detalle && <p className="mt-2 text-sm text-[var(--tinta-suave)]">{detalle}</p>}

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onCancelar}
            className="min-h-12 flex-1 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] font-medium"
            data-testid="accion-cancelar"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirmar}
            disabled={ocupado}
            className="min-h-12 flex-1 rounded-[var(--radius-control)] bg-[var(--accion)] font-medium text-[var(--sobre-accion)] disabled:opacity-50 "
            data-testid="accion-confirmar"
          >
            {ocupado ? 'Guardando…' : confirmar}
          </button>
        </div>
      </div>
    </div>
  );
}
